import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useCreateTransaction } from '../lib/transactions'
import { useCategories } from '../lib/categories'
import { useCards } from '../lib/cards'

export interface TransactionFormProps {
  onSuccess?: () => void
  initialType?: 'income' | 'expense'
}

export default function TransactionForm({ onSuccess, initialType = 'income' }: TransactionFormProps) {
  const [type, setType] = useState<'income' | 'expense'>(initialType)
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState('')
  const [transactionDate, setTransactionDate] = useState(() => new Date().toISOString().split('T')[0])
  const [categoryId, setCategoryId] = useState('')
  const [cardId, setCardId] = useState('')

  const { data: categoriesData, isLoading: categoriesLoading } = useCategories()
  const { data: cardsData } = useCards()

  const createTransaction = useCreateTransaction()
  const queryClient = useQueryClient()

  const categories = categoriesData?.categories ?? []
  const cards = cardsData?.cards ?? []

  const incomeCategories = categories.filter((c) => c.type === 'income')
  const expenseCategories = categories.filter((c) => c.type === 'expense')

  const filteredCategories = type === 'income' ? incomeCategories : expenseCategories

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!categoryId || !amount) {
      return
    }

    createTransaction.mutate({
      description: description.trim() || '',
      amount,
      transaction_date: transactionDate,
      category_id: categoryId,
      type,
      card_id: cardId || null,
    }, {
      onSuccess: () => {
        setDescription('')
        setAmount('')
        setTransactionDate(new Date().toISOString().split('T')[0])
        setCategoryId('')
        setCardId('')
        setType('income')
        queryClient.invalidateQueries({ queryKey: ['transactions'] })
        queryClient.invalidateQueries({ queryKey: ['transactions-summary'] })
        onSuccess?.()
      },
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Type selector */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text)]">Tipo</label>
        <div className="mt-1 flex gap-2">
          <button
            type="button"
            onClick={() => setType('income')}
            className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              type === 'income'
                ? 'bg-[var(--color-success)] text-white'
                : 'bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-subtle)]'
            }`}
          >
            Entrada
          </button>
          <button
            type="button"
            onClick={() => setType('expense')}
            className={`flex-1 rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              type === 'expense'
                ? 'bg-[var(--color-danger)] text-white'
                : 'bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface-subtle)]'
            }`}
          >
            Saída
          </button>
        </div>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text)]">Descrição</label>
        <input
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Ex: Salário, Supermercado, Comida..."
          className="mt-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)] w-full"
        />
      </div>

      {/* Amount */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text)]">Valor (R$)</label>
        <input
          type="number"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder="0.00"
          className="mt-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] placeholder-[var(--color-text-muted)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)] w-full"
          required
        />
      </div>

      {/* Date */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text)]">Data</label>
        <input
          type="date"
          value={transactionDate}
          onChange={(e) => setTransactionDate(e.target.value)}
          className="mt-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)] w-full"
          required
        />
      </div>

      {/* Category */}
      <div>
        <label className="block text-sm font-medium text-[var(--color-text)]">Categoria</label>
        {categoriesLoading ? (
          <div className="mt-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2">
            <span className="text-[var(--color-text-muted)]">Carregando...</span>
          </div>
        ) : (
          <select
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="mt-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)] w-full"
            required
          >
            <option value="">Selecione...</option>
            {filteredCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Card (optional) */}
      {cards.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-[var(--color-text)]">Cartão (opcional)</label>
          <select
            value={cardId}
            onChange={(e) => setCardId(e.target.value)}
            className="mt-1 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm text-[var(--color-text)] focus:border-[var(--color-brand-400)] focus:ring-1 focus:ring-[var(--color-brand-400)] w-full"
          >
            <option value="">Nenhum cartão</option>
            {cards.map((card) => (
              <option key={card.id} value={card.id}>
                {card.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={createTransaction.isPending || !categoryId || !amount}
        className="w-full rounded-lg bg-[var(--color-brand-500)] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--color-brand-600)] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {createTransaction.isPending ? 'Salvando...' : 'Salvar transação'}
      </button>
    </form>
  )
}